// AI assisted development
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { ProxyService } from './proxy-service';
import { ContextService } from './context-service';
import { TokenUtilsService } from './token-utils-service';
import { LoggerService } from './logger-service';
import { AppAttributeEnum } from '../enums/appAttribute.enum';
import { IResponse } from 'src/app/@shared/models/response';
import {
  IConsolidatedSubsApprovalRequest,
  IGetConsolidatedSubsResponse,
  mapGetConsolidatedSubsResponse
} from '../models/consolidated-subs';

@Injectable()
export class ConsolidatedSubsService {

  private consolidatedSubsBaseUrlPromise: Promise<string>;

  constructor(
    private httpClient: HttpClient,
    private proxyService: ProxyService,
    private contextService: ContextService,
    private tokenUtilsService: TokenUtilsService,
    private loggerService: LoggerService
  ) {}

  async getConsolidatedSubs(storeSourceKey: string, orderSourceKey: string): Promise<IGetConsolidatedSubsResponse> {
    const baseUrl = await this.getConsolidatedSubsBaseUrl();
    const url = `${baseUrl}${encodeURIComponent(orderSourceKey)}`;
    const response = await this.get(url, storeSourceKey);
    return mapGetConsolidatedSubsResponse(response);
  }

  async submitConsolidatedSubs(
    storeSourceKey: string,
    orderSourceKey: string,
    request: IConsolidatedSubsApprovalRequest
  ): Promise<IResponse> {
    const baseUrl = await this.getConsolidatedSubsBaseUrl();
    const url = `${baseUrl}store/${encodeURIComponent(storeSourceKey)}/order/${encodeURIComponent(orderSourceKey)}/sub-approvals`;
    return await this.post(url, request, storeSourceKey);
  }

  private getConsolidatedSubsBaseUrl(): Promise<string> {
    if (!this.consolidatedSubsBaseUrlPromise) {
      this.consolidatedSubsBaseUrlPromise = this.fetchConsolidatedSubsBaseUrl();
    }
    return this.consolidatedSubsBaseUrlPromise;
  }

  private async fetchConsolidatedSubsBaseUrl(): Promise<string> {
    const clientId = this.contextService.userContext().clientId;
    const attr = await this.proxyService.getCore(`client/${clientId}/appattributes/${AppAttributeEnum.BaseSkOrmServiceUri}`, 0);
    const baseUri = ((attr && attr.value) || '').replace(/\/+$/, '');
    return `${baseUri}/api/v1/consolidatedsubs/`;
  }

  private createHeaders(storeSourceKey: string): HttpHeaders {
    const token = this.contextService.userContext().token;
    this.tokenUtilsService.validateTokenOrThrow(token);

    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    headers = headers.append('Accept', 'application/json, application/javascript');
    headers = headers.append('Authorization', 'fulfiller ' + token);
    headers = headers.append('fulfiller-storeId', storeSourceKey);
    headers = headers.append('fulfiller-clientId', this.contextService.userContext().clientId ?
      this.contextService.userContext().clientId.toString() : '0');
    return headers;
  }

  private get(fullUrl: string, storeSourceKey: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const headers = this.createHeaders(storeSourceKey);
      this.httpClient.get(fullUrl, { headers: headers }).toPromise()
        .then(response => { resolve(response); })
        .catch(error => {
          this.loggerService.logMessage('in consolidated subs service log error');
          this.loggerService.logException(error);
          reject(error);
        });
    });
  }

  private post(fullUrl: string, body: any, storeSourceKey: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const headers = this.createHeaders(storeSourceKey);
      this.httpClient.post(fullUrl, body, { headers: headers }).toPromise()
        .then(response => { resolve(response); })
        .catch(error => {
          this.loggerService.logMessage('in consolidated subs service log error');
          this.loggerService.logException(error);
          reject(error);
        });
    });
  }
}
